import { Injectable, Inject } from '@angular/core';
import { DOCUMENT, registerLocaleData } from '@angular/common';
import { Globals } from './globals.service';
import { Log } from './app-config.service';

interface DomainConfig {
    localeId: string;
    currencyCode: string;
    localeKey: string;      // chiave per import dinamico
    translationKey: string; // chiave per file i18n
}

@Injectable({
    providedIn: 'root'
})
export class InitAppService {
    
    private domainConfigMap: { [key: string]: DomainConfig } = {
        'it': { localeId: 'it-IT', currencyCode: 'EUR', localeKey: 'it', translationKey: 'it' },
        'fr': { localeId: 'fr-FR', currencyCode: 'EUR', localeKey: 'fr', translationKey: 'fr' },
        'es': { localeId: 'es-ES', currencyCode: 'EUR', localeKey: 'es', translationKey: 'es' },
        'de': { localeId: 'de-DE', currencyCode: 'EUR', localeKey: 'de', translationKey: 'de' },
        'com': { localeId: 'en-US', currencyCode: 'EUR', localeKey: 'en', translationKey: 'en' },
        'co.uk': { localeId: 'en-GB', currencyCode: 'GBP', localeKey: 'en', translationKey: 'en' },
    };

    private currentLocaleId: string = 'it-IT';
    private currentCurrencyCode: string = 'EUR';

    constructor(
        @Inject(DOCUMENT) private _doc: Document,
        private globals: Globals
    ) { }

    private getDomainFromHostname(): string {
        const hostname_split: string[] = this._doc.location.hostname.split('.');
        let hostname_domain: string = hostname_split[hostname_split.length - 1].toLowerCase();

        if (hostname_domain === 'uk') {
            hostname_domain = hostname_split[hostname_split.length - 2] + '.' + hostname_domain;
        }

        if (this._doc.location.hostname === '127.0.0.1') {
            hostname_domain = 'it';
        }

        return hostname_domain;
    }

    get localeId(): string {
        return this.currentLocaleId;
    }

    get currencyCode(): string {
        return this.currentCurrencyCode;
    }

    InitWpromise(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            try {
                const hostname_domain = this.getDomainFromHostname();
                this.globals.setGlobalVar(hostname_domain);
                resolve();
            } catch (error) {
                Log.error('Error in InitWpromise:', error);
                reject(error);
            }
        });
    }

    async LocalizeWpromise(): Promise<string> {
        try {
            const hostname_domain = this.getDomainFromHostname();
            const config = this.domainConfigMap[hostname_domain] || this.domainConfigMap['it'];

            // Carica dinamicamente locale e traduzioni in parallelo
            const [localeModule, translationModule] = await Promise.all([
                this.loadLocaleData(config.localeKey),
                this.loadTranslationData(config.translationKey)
            ]);


            // Registra il locale
            registerLocaleData(localeModule.default, config.localeId);

            // Salva configurazione corrente
            this.currentLocaleId = config.localeId;
            this.currentCurrencyCode = config.currencyCode;

            // Imposta <html lang> in base al dominio (vale anche in SSR → HTML corretto per ogni ccTLD)
            this._doc.documentElement.lang = config.localeId;

            // Imposta traduzione globale
            this.globals.setGlobalTraduction(translationModule.default);

            return config.localeId;

        } catch (error) {
            Log.error('Error in LocalizeWpromise:', error);
            // Fallback italiano
            const fallbackLocale = await import('@angular/common/locales/it');
            const fallbackTranslation = await import('public/i18n/it.json');
            
            registerLocaleData(fallbackLocale.default, 'it-IT');
            this.globals.setGlobalTraduction(fallbackTranslation.default);
            this._doc.documentElement.lang = 'it-IT';

            return 'it-IT';
        }
    }

    private loadLocaleData(key: string): Promise<any> {
        switch (key) {
            case 'it': return import('@angular/common/locales/it');
            case 'fr': return import('@angular/common/locales/fr');
            case 'es': return import('@angular/common/locales/es');
            case 'de': return import('@angular/common/locales/de');
            case 'en': return import('@angular/common/locales/en');
            default: return import('@angular/common/locales/it');
        }
    }

    private loadTranslationData(key: string): Promise<any> {
        switch (key) {
            case 'it': return import('public/i18n/it.json');
            case 'fr': return import('public/i18n/fr.json');
            case 'es': return import('public/i18n/es.json');
            case 'de': return import('public/i18n/de.json');
            case 'en': return import('public/i18n/en.json');
            default: return import('public/i18n/it.json');
        }
    }
}